import { useState, useMemo } from "react";
import { Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useSizing, modelForProduct, findTable } from "@/lib/sizing-store";
import { PrimaryButton } from "@/components/design-system";
import { AssignmentDialogShell } from "./AssignmentDialogShell";

/**
 * ProductModelDialog — Model + worn-size picker. Rows behave like the fit /
 * image dialogs; clicking a model expands its row to reveal an inline
 * "giydiği beden" input + suggestion chips.  Selecting a size (chip or Enter
 * in the input) applies the assignment immediately and closes the dialog.
 *
 * Uses its own custom row layout (not AssignOptionRow) because of the inline
 * expand/collapse for the worn-size input, but keeps the same visual family
 * (bg-card + border + shadow-sm) as the shell body.
 */
export const ProductModelDialog = ({ product, onClose }) => {
    const { models, products, setProductModel, tables, links } = useSizing();
    const [expanded, setExpanded] = useState(null);   // id of the model whose size input is open
    const [size, setSize] = useState("");
    const [query, setQuery] = useState("");
    const [saving, setSaving] = useState(false);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return models;
        return models.filter((m) => (m.name || "").toLowerCase().includes(q));
    }, [models, query]);

    if (!product) return null;
    const current = modelForProduct(models, product.id, products);
    const currentModelId = current?.model.id || null;
    const tbl = links[product.id] ? findTable(tables, links[product.id]) : null;
    const suggestions = tbl?.sizes ?? [];

    const openRow = (mid) => {
        if (expanded === mid) {
            setExpanded(null);
            return;
        }
        setExpanded(mid);
        // Preselect the currently-attached size when re-opening the assigned model,
        // otherwise clear so the merchant makes an intentional pick.
        if (mid === currentModelId && current?.size) setSize(current.size);
        else setSize("");
    };

    const apply = async (mid, sz) => {
        if (saving) return;
        const wornSize = (sz || "").trim();
        if (!wornSize) {
            toast.error("Modelin giydiği bedeni yaz");
            return;
        }
        setSaving(true);
        try {
            await setProductModel(product.id, mid, wornSize);
            const m = models.find((x) => x.id === mid);
            toast.success(`${m?.name} · ${wornSize} bedeni giyiyor`);
            onClose();
        } catch {
            toast.error("Model güncellenemedi");
        } finally {
            setSaving(false);
        }
    };

    const clear = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await setProductModel(product.id, null, null);
            toast.success("Model ataması kaldırıldı");
            onClose();
        } catch {
            toast.error("Model güncellenemedi");
        } finally {
            setSaving(false);
        }
    };

    return (
        <AssignmentDialogShell
            title="Model ataması"
            product={product}
            onClose={onClose}
            searchable
            searchValue={query}
            onSearchChange={setQuery}
            searchPlaceholder="Model ara…"
            onClear={current ? clear : null}
            testId="product-model-dialog"
        >
            {models.length === 0 ? (
                <div className="text-center py-10 text-xs text-muted-foreground">
                    Henüz model yok.{" "}
                    <a href="/app/sizing/models" target="_blank" rel="noopener" className="underline hover:text-foreground">
                        Modeller sayfasından
                    </a>{" "}
                    ekle.
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-8 text-xs text-muted-foreground">Sonuç yok</div>
            ) : (
                filtered.map((m) => {
                    const isCurrent = currentModelId === m.id;
                    const isOpen = expanded === m.id;
                    const initials = m.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
                    return (
                        <div key={m.id} className={`rounded-lg bg-card border shadow-sm transition-all ${isCurrent && !isOpen ? "border-foreground shadow-md" : "border-border hover:border-foreground/40 hover:shadow-md"}`}>
                            <button
                                type="button"
                                onClick={() => openRow(m.id)}
                                disabled={saving}
                                className="w-full text-left flex items-center gap-3 p-3 rounded-lg transition-colors"
                                data-testid={`pm-${m.id}`}
                            >
                                <span className="h-10 w-10 rounded-md bg-foreground text-background grid place-items-center font-display font-bold text-xs shrink-0">
                                    {initials}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium">{m.name}</div>
                                    <div className="text-[10.5px] text-muted-foreground truncate">
                                        {m.height} cm{isCurrent && current?.size ? ` · şu an ${current.size} bedeni giyiyor` : ""}
                                    </div>
                                </div>
                                {isCurrent && <Check className="h-4 w-4 text-brand shrink-0" strokeWidth={2.5} />}
                            </button>
                            {isOpen && (
                                <div className="px-4 pb-4 pt-2 space-y-2 border-t border-border" data-testid={`pm-size-row-${m.id}`}>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            value={size}
                                            onChange={(e) => setSize(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(m.id, size); } }}
                                            placeholder="Beden (ör. M, 38, 32…)"
                                            className="h-8 text-xs bg-background flex-1"
                                            autoFocus
                                            data-testid={`pm-size-input-${m.id}`}
                                        />
                                        <PrimaryButton
                                            onClick={() => apply(m.id, size)}
                                            disabled={saving || !size.trim()}
                                            data-testid={`pm-apply-${m.id}`}
                                            className="h-8 px-3 text-xs"
                                        >
                                            Ata
                                        </PrimaryButton>
                                    </div>
                                    {suggestions.length > 0 && (
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-[10px] text-muted-foreground">Tablodan öneri:</span>
                                            {suggestions.map((sz) => (
                                                <button
                                                    key={sz}
                                                    type="button"
                                                    onClick={() => apply(m.id, sz)}
                                                    disabled={saving}
                                                    data-testid={`pm-suggest-${sz}`}
                                                    className={`h-6 min-w-6 px-1.5 rounded border text-[11px] font-medium transition-colors ${size === sz ? "border-brand bg-brand text-brand-fg" : "border-border hover:border-foreground/40"}`}
                                                >
                                                    {sz}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </AssignmentDialogShell>
    );
};
